/* eslint-disable no-console */
import React, { useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Anchor, Button, Group, Text as MantineText, Checkbox } from '@mantine/core';
import { Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { CAMEL_TYPES, MENU_OBJECT_TYPES, AUTO_MAPPING_COMPLETE } from 'constants/menuManagement';

import {
  applyMappingData,
  fetchMappingFormattedMenu,
  fetchMappingFormattedPosMenu,
  buildMappingMessageText,
  handleAutoMapRequest,
  formatPrice,
} from 'helpers/mapping';
import { setAppMenuWithChecked, setEditedItemsWithChecked } from 'helpers/mappingColumns';
import { trackMenuMappingSaved } from 'helpers/menuAnalytics';
import { DEFAULT_MODIFIERS, SHOW_DEFAULT_MODIFIERS, MAPPING_STATUS } from 'constants/mapping';
import { NOTIFICATION_VARIANTS } from 'constants/chownow';

import { usePusher } from 'hooks/usePusher';

import MainContentLoader from 'primitives/MainContentLoader/MainContentLoader';
import MainItemTable from 'components/MenuManagement/MenuEdit/MenuMapping/MainItemMapping';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import ModifierTable from 'components/MenuManagement/MenuEdit/MenuMapping/ModifierMapping';
import PanelHeader from './PanelHeader';

const { Text } = Typography;

export const MenuMappingContext = React.createContext();

export default function MenuMapping() {
  const [isLoading, setIsLoading] = useState(true);
  const [visibleTab, setVisibleTab] = useState(CAMEL_TYPES.items);
  const [appMenu, setAppMenu] = useState({});
  const [posMenu, setPosMenu] = useState({});
  const [editedItems, setEditedItems] = useState({});
  const [allowSelection, setAllowSelection] = useState(false);
  const [isAwaitingProcess, setIsAwaitingProcess] = useState(false);
  const [asyncProcess, setAsyncProcess] = useState('unknown');
  const [checkedItems, setCheckedItems] = useState([]);

  const { menu } = useContext(MenuEdit.Context);

  const lastMenuIdRef = useRef(null);
  const isFetchingRef = useRef(false);
  // Tracks whether auto-mapping completed during this session so menu_mapping_saved can
  // distinguish 'manual' (user edits only) from 'mixed' (auto-map ran, then user edits).
  const autoMapRanRef = useRef(false);

  const { channels } = usePusher();
  const channel = channels?.find(
    each => each.name === `${import.meta.env.VITE_WEB_SOCKET_STAGE}-company-${menu.companyId}`,
  );

  useEffect(() => {
    setCheckedItems([]);
  }, [visibleTab]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const companyID = menu?.companyId;
    const merchantID = menu?.merchantId;
    const menuID = menu?.uuid;
    let appItems = {};
    let appModifiers = {};
    try {
      appItems = await fetchMappingFormattedMenu({
        companyID,
        merchantID,
        menuID,
        itemType: MENU_OBJECT_TYPES.items,
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching app items', error);
    }
    try {
      appModifiers = await fetchMappingFormattedMenu({
        companyID,
        merchantID,
        menuID,
        itemType: MENU_OBJECT_TYPES.modifiers,
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching app modifiers', error);
    }
    setAppMenu({
      [CAMEL_TYPES.items]: appItems,
      [CAMEL_TYPES.modifiers]: appModifiers,
    });
    let posItems = {};
    let posModifiers = {};
    try {
      posItems = await fetchMappingFormattedPosMenu({
        companyID,
        merchantID,
        itemType: MENU_OBJECT_TYPES.items,
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching pos items', error);
    }
    try {
      posModifiers = await fetchMappingFormattedPosMenu({
        companyID,
        merchantID,
        itemType: MENU_OBJECT_TYPES.modifiers,
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching pos modifiers', error);
    }
    setPosMenu({
      [CAMEL_TYPES.items]: posItems,
      [CAMEL_TYPES.modifiers]: posModifiers,
    });
    setIsLoading(false);
    isFetchingRef.current = false;
  }, [menu]);

  const handleRefetch = useCallback(() => {
    setEditedItems({});
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!channel) return;
    channel.unbind(AUTO_MAPPING_COMPLETE);
    channel.bind(AUTO_MAPPING_COMPLETE, async payload => {
      if (menu.uuid === payload.menu_uuid) {
        autoMapRanRef.current = true;
        setIsAwaitingProcess(false);
        setIsLoading(true);
        handleRefetch();
      }
    });
    // menu.uuid must be a dep -- channel alone only changes when menu.companyId does.
  }, [channel, menu.uuid, handleRefetch]);

  const handleSave = useCallback(async () => {
    // TODO: CUBOH-1871 Add error handling
    const mappingData = { ...editedItems };
    delete mappingData.null;
    delete mappingData.control;
    const companyID = menu?.companyId;
    const merchantID = menu?.merchantId;
    const menuID = menu?.uuid;

    // The compiler cannot support conditionals/optional-chaining inside a try/catch, so the
    // try is kept to just the risky call and every branch lives outside it.
    let result;
    try {
      result = await applyMappingData({ companyID, merchantID, menuID, mappingData });
    } catch (error) {
      notifications.show({
        message: 'Your edit could not be processed. Please contact customer support.',
        variant: NOTIFICATION_VARIANTS.caution.label,
        icon: NOTIFICATION_VARIANTS.caution.icon,
        style: { marginTop: 'var(--mantine-spacing-sm)' },
      });
      return;
    }

    if (!result || !result.result) return;

    const mappedItemCount = Object.keys(mappingData).length;
    const userEdited = mappedItemCount > 0;
    let mappingType = 'manual';
    if (autoMapRanRef.current) {
      if (userEdited) {
        mappingType = 'mixed';
      } else {
        mappingType = 'auto';
      }
    }
    trackMenuMappingSaved({
      menuId: menuID,
      mappedItemCount,
      mappingType,
    });

    handleRefetch();
    const { successMessage } = buildMappingMessageText({
      appMenu,
      posMenu,
      itemType: visibleTab,
    });
    notifications.show({
      message: `You have successfully ${successMessage}`,
      variant: NOTIFICATION_VARIANTS.success.label,
      icon: NOTIFICATION_VARIANTS.success.icon,
      style: { marginTop: 'var(--mantine-spacing-sm)' },
    });
    setCheckedItems([]);
  }, [editedItems, menu, handleRefetch, setCheckedItems, appMenu, posMenu, visibleTab]);

  const handleAutoMap = useCallback(
    async ({ forceMap, unmapAll }) => {
      let message = 'Auto-Map Unmapped';
      if (forceMap) message = 'Auto-Map All';
      if (unmapAll) message = 'Unmap All';
      setAsyncProcess(message);
      setIsAwaitingProcess(true);

      const companyID = menu?.companyId;
      const merchantID = menu?.merchantId;
      const menuUuid = menu?.uuid;
      try {
        await handleAutoMapRequest({
          companyID,
          merchantID,
          menuUuid,
          itemType: visibleTab,
          forceMap,
          unmapAll,
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error requesting auto mapping', error);
      }
    },
    [menu, visibleTab],
  );

  const handleTabChange = value => setVisibleTab(value);

  useEffect(() => {
    if (!menu?.uuid || menu.uuid === lastMenuIdRef.current || isFetchingRef.current) {
      return;
    }

    lastMenuIdRef.current = menu.uuid;
    isFetchingRef.current = true;
    setAppMenu({});
    setPosMenu({});
    setEditedItems({});
    fetchData();
  }, [menu?.uuid]);

  const buildMappingMessage = useCallback(() => {
    const { baseMessage, saveRequired } = buildMappingMessageText({
      appMenu,
      posMenu,
      itemType: visibleTab,
    });
    return `${baseMessage}${saveRequired}`;
  }, [appMenu, visibleTab, posMenu]);

  const allowSelectionCheckbox = () => (
    <Checkbox
      label="Allow all selections"
      checked={allowSelection}
      onChange={() => setAllowSelection(!allowSelection)}
    />
  );

  const selectContent = () => {
    if (visibleTab === CAMEL_TYPES.items) {
      return <MainItemTable />;
    }
    if (visibleTab === CAMEL_TYPES.modifiers) {
      return <ModifierTable />;
    }
    return <>No content</>;
  };

  const renderMappingActionButtons = (
    <>
      <Anchor
        component="button"
        variant="sublabel"
        size="sm"
        underline="always"
        onClick={handleRefetch}
      >
        Reset
      </Anchor>
      <Group gap="xs">
        <MantineText variant="sublabel-sm">{buildMappingMessage()}</MantineText>
        <Button size="md" type="submit" w="140" style={{ flexGrow: 1 }} onClick={handleSave}>
          Save
        </Button>
      </Group>
    </>
  );
  const setDefaultMods = ({ rowKey, value }) => {
    const itemType = CAMEL_TYPES.items;

    const showDefaultModifiers = !!value;
    let valueList = value;
    if (valueList instanceof Array) {
      valueList = value.join(',');
    }
    setEditedItemsWithChecked({
      key: rowKey,
      updatedData: {
        ...appMenu[itemType][rowKey],
        ...editedItems[rowKey],
        [DEFAULT_MODIFIERS]: valueList,
      },
      checkedItems,
      setEditedItems,
      editedItems,
    });
    setAppMenuWithChecked({
      key: rowKey,
      itemType,
      updatedData: {
        ...appMenu[itemType][rowKey],
        [DEFAULT_MODIFIERS]: valueList,
        mappingStatus: appMenu[itemType][rowKey]
          ? MAPPING_STATUS.STAGED_MAPPED
          : MAPPING_STATUS.STAGED_UNMAPPED,
        [SHOW_DEFAULT_MODIFIERS]: showDefaultModifiers,
      },
      checkedItems,
      setAppMenu,
      appMenu,
    });
  };

  const contextValue = useMemo(
    () => ({
      posMenu,
      appMenu,
      setAppMenu,
      editedItems,
      setEditedItems,
      handleAutoMap,
      handleSave,
      buildMappingMessage,
      allowSelectionCheckbox,
      allowSelection,
      formatPrice,
      renderMappingActionButtons,
      checkedItems,
      setCheckedItems,
      setDefaultMods,
    }),
    [
      posMenu,
      appMenu,
      setAppMenu,
      editedItems,
      setEditedItems,
      handleAutoMap,
      handleSave,
      buildMappingMessage,
      allowSelectionCheckbox,
      allowSelection,
      formatPrice,
      renderMappingActionButtons,
      checkedItems,
      setCheckedItems,
      setDefaultMods,
    ],
  );

  if (isAwaitingProcess) return <AwaitingProcess asyncProcess={asyncProcess} />;

  if (isLoading) return <MainContentLoader />;

  return (
    <MenuMappingContext.Provider value={contextValue}>
      <PanelHeader visibleTab={visibleTab} handleTabChange={handleTabChange} />
      {selectContent()}
    </MenuMappingContext.Provider>
  );
}

function AwaitingProcess({ asyncProcess }) {
  return (
    <Space direction="vertical">
      <Text>{`Awaiting completion of ${asyncProcess} process`}</Text>
      <Text type="secondary">This may take a moment</Text>
    </Space>
  );
}

AwaitingProcess.propTypes = {
  asyncProcess: PropTypes.string,
};
